import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { ComposeComponentOptions, ComposePage } from 'src/app/components/compose/compose.page';
import { OUTBOUND_STATUS, routes } from 'src/app/constants';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { subscribeToSameUrlReload } from 'src/app/utils/router-utils';
import { removeUndefinedFields } from 'src/app/utils/utils';
import { CONTEXTUAL_AUTHORIZATION_REQUIREMENTS } from 'src/constants/contextual-authorization.constants';
import { PERMISSIONS_V2_FEATURE_FLAG } from 'src/constants/feature-flags.constants';
import { ListQueryResult } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { ContextualAuthorizationService } from 'src/services/contextual-authorization.service';
import { MessageService } from 'src/services/message.service';

import { TextPopoverComponent } from '../text-popover/text-popover.component';

import { MessageAnalyticsPage } from './message-analytics/message-analytics.page';

@Component({
  selector: 'app-messaging',
  templateUrl: './messaging.component.html',
  styleUrls: ['./messaging.component.scss'],
})
export class MessagingComponent implements OnInit, OnDestroy {
  @ViewChild('messagesTable') table: DatatableComponent;
  projectId: string | null = null;
  selected = [];
  messages = [];
  filter = 'all';
  isLoading = false;
  ColumnMode = ColumnMode;
  routerSub: Subscription;
  hasAllSlackScopes = false;
  pendingOutbound: string;
  filters: SelectedFilterMap = new Map();
  filterOptions: FilterOption[] = [
    {
      label: 'Source',
      id: 'deliveryMethod',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
      ],
    },
    {
      label: 'Sender',
      id: 'createdBy',
      icon: 'person',
      type: 'select',
      searchablePopover: 'users',
    },
    {
      label: 'Date Sent',
      id: 'sendAt',
      icon: 'calendar',
      type: 'date',
    },
    {
      label: 'Recipient Channel',
      id: 'recipientChannelId',
      icon: 'chatbubble',
      type: 'select',
      searchablePopover: 'slackChannels',
    },
  ];
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiService,
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private navController: NavController,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private contextualAuthorizationService: ContextualAuthorizationService,
  ) {}

  get canManageBroadcasts(): boolean {
    return this.canManageBroadcast();
  }

  canManageBroadcast(outbound?: any): boolean {
    if (!this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG)) {
      return true;
    }

    const scope = outbound?.projectId === undefined ? this.projectId : outbound.projectId;

    return this.contextualAuthorizationService.isAuthorized(
      scope,
      CONTEXTUAL_AUTHORIZATION_REQUIREMENTS.MANAGE_BROADCASTS,
    );
  }

  canAccessReporting(outbound: any): boolean {
    if (!this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG)) {
      return true;
    }

    const scope = outbound.projectId === undefined ? this.projectId : outbound.projectId;

    return this.contextualAuthorizationService.isAuthorized(
      scope,
      CONTEXTUAL_AUTHORIZATION_REQUIREMENTS.ACCESS_REPORTING,
    );
  }

  async ngOnInit() {
    const routeProjectId =
      this.route.snapshot.paramMap.get('projectId') ?? this.route.parent?.snapshot.paramMap.get('projectId');

    this.projectId = routeProjectId ?? this.authService.selectedProject?.id ?? null;

    this.routerSub = subscribeToSameUrlReload(this.router, this.location, () => this.getOutboundList());

    this.pendingOutbound = this.route.snapshot.queryParamMap.get('outboundId');

    this.getOutboundList();
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getOutboundList() {
    const queryParams: any = {};

    if (this.filter !== 'all') {
      queryParams.status = this.filter;
    }

    if (this.projectId) {
      queryParams.projectId = this.projectId;
    }

    // Apply filters from the filter button
    for (const [filterOption, filterValues] of this.filters.entries()) {
      if (filterValues && filterValues.length > 0) {
        if (filterOption.type === 'date' || filterOption.type === 'datetime') {
          // Date filters can come in two formats:
          // 1. Relative: Single value with JSON string containing { type: 'relative', period, startIndex, ... }
          // 2. Absolute: Two values with ISO date strings [startDate, endDate]
          const firstValue = filterValues[0].id;

          // Check if it's a JSON object (relative date) or ISO string (absolute date)
          if (typeof firstValue === 'string' && firstValue.startsWith('{')) {
            // Relative date - pass the JSON directly
            queryParams[filterOption.id] = firstValue;
          } else if (filterValues.length >= 2) {
            // Absolute date - construct JSON object from the two date values
            queryParams[filterOption.id] = JSON.stringify({
              type: 'absolute',
              startDate: filterValues[0].id,
              endDate: filterValues[1].id,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
          }
        } else {
          // Join filter values with comma
          queryParams[filterOption.id] = filterValues.map((v) => v.id).join(',');
        }
      }
    }

    this.isLoading = true;

    try {
      const result: ListQueryResult = await this.apiService.getPromise(`/outbounds`, queryParams);

      this.messages = result.rows.map((outbound) => {
        if (this.pendingOutbound && this.pendingOutbound === outbound.id) {
          if (this.canAccessReporting(outbound)) {
            this.viewAnalytics(outbound);
          }

          this.pendingOutbound = undefined;
        }

        let groupCount = 0;
        let peopleCount = 0;

        if (outbound.deliveryMethod === 'email') {
          outbound.recipients?.forEach((r) => {
            if (r.count) {
              peopleCount += r.count;
              groupCount++;
            } else {
              peopleCount++;
            }
          });
          peopleCount += (outbound.metadata?.cc?.length ?? 0) + (outbound.metadata?.bcc?.length ?? 0);
        } else {
          outbound.recipients?.forEach((r) => {
            peopleCount += r.followerCount ?? 0;
            groupCount++;
          });
          groupCount = outbound.recipients?.length;
        }

        return {
          ...outbound,
          sendAt: new Date(outbound.runAt ?? outbound.createdAt),
          groupCount,
          peopleCount,
        };
      });

      // HACK: Fix for issue where message would be marked as "sending" until the page is refreshed. Check if there are any very recently created messages
      // that are "sending" and refresh the messages. Will only attempt for an hour after the message was created so we don't end up in situation where
      // we refresh forever waiting on an actually stuck message.
      if (
        this.messages.some(
          (m) =>
            m.status === OUTBOUND_STATUS.SCHEDULED &&
            !m.runAt &&
            moment(m.createdAt).isAfter(moment().subtract(1, 'hours')),
        )
      ) {
        setTimeout(() => {
          this.getOutboundList();
        }, 1000);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async newEmailMessage() {
    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        emails: [],
        showTemplates: true,
        projectId: this.projectId,
        callback: () => {
          this.getOutboundList();
        },
      },
      backdropDismiss: false,
    });
    modal.present();
  }

  async editNotes(outbound: any) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: outbound.notes,
        shouldSelect: true,
        isMultiline: true,
        callback: async (text: string) => {
          this.isLoading = true;

          const request = this.apiService.put(`/outbounds/${outbound.id}`, {
            ...outbound,
            notes: text,
          });

          request.subscribe(
            async (_result) => {
              this.isLoading = false;
              outbound.notes = text;
            },
            async (err) => {
              this.msgSrvc.show(err.message || "That didn't work, please try again.");
              this.isLoading = false;
            },
          );
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async viewAnalytics(outbound: any) {
    this.analyticsService.trackEvent('Messaging', 'View outbound analytics', {
      outboundId: outbound.id,
      deliveryMethod: outbound.deliveryMethod,
    });

    const modal = await this.modalCtrl.create({
      component: MessageAnalyticsPage,
      componentProps: {
        outbound,
      },
      cssClass: 'modal-large',
    });

    modal.present();
  }

  async editMessage(outbound: any) {
    if (outbound.deliveryMethod === 'email') {
      this.editEmailMessage(outbound);
    } else {
      this.editSlackMessage(outbound);
    }
  }

  async editEmailMessage(outbound: any) {
    const projectId = outbound.projectId === undefined ? this.projectId : outbound.projectId;
    const options: ComposeComponentOptions = {
      title:
        outbound.status === OUTBOUND_STATUS.DRAFT
          ? 'Compose email'
          : outbound.status === OUTBOUND_STATUS.SCHEDULED
            ? 'Edit scheduled email'
            : 'Review sent email',
      hideEmails: false,
    };

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        showTemplates: outbound.status === 'draft',
        options,
        draft: outbound,
        projectId,
        callback: () => {
          this.getOutboundList();
        },
      },
      backdropDismiss: false,
    });

    modal.present();
  }

  async newSlackMessage() {
    if (
      (this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG) || this.authService.isNewNavEnabled()) &&
      this.projectId
    ) {
      this.navController.navigateForward(`${routes.DASHBOARD}/projects/${this.projectId}/broadcasts/new`, {
        queryParams: removeUndefinedFields({
          previousUrl: this.location.path(),
          customerIds: [],
        }),
      });
    } else {
      this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
        queryParams: removeUndefinedFields({
          previousUrl: this.location.path(),
          customerIds: [],
          projectId: this.projectId,
        }),
      });
    }
  }

  async editSlackMessage(outbound: any) {
    const permissionsV2Enabled = this.authService.isFeatureEnabled(PERMISSIONS_V2_FEATURE_FLAG);
    const projectId = outbound.projectId === undefined ? this.projectId : outbound.projectId;

    if ((permissionsV2Enabled || this.authService.isNewNavEnabled()) && projectId) {
      this.navController.navigateForward(`${routes.DASHBOARD}/projects/${projectId}/broadcasts/${outbound.id}`, {
        queryParams: {
          previousUrl: this.location.path(),
        },
      });
    } else {
      this.navController.navigateForward(`${routes.SLACK_MESSAGE}/${outbound.id}`, {
        queryParams: {
          previousUrl: this.location.path(),
          projectId: permissionsV2Enabled ? projectId : this.projectId,
        },
      });
    }
  }

  viewTemplates() {
    if (this.projectId) {
      this.navController.navigateRoot(`${routes.DASHBOARD}/projects/${this.projectId}/setup/templates`);
    } else {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.OUTBOUND}/templates`);
    }
  }

  async showError(outbound: any) {
    if (!outbound.error) {
      return;
    }

    const name = `Failed to send outbound`;
    const note = `"${outbound.error}"`;
    const alert = await this.alertCtrl.create({
      header: name,
      subHeader: note,
    });

    alert.present();
  }

  async filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.getOutboundList();
  }
}
